import { createEffect, createSignal, type JSX, untrack } from 'solid-js';
import { t } from '../i18n';
import { Field } from './Field';
import { Modal } from './Modal';
import { Button, buttonClass } from './ui/Button';

// Rename-a-board dialog (#1033). Presenter — the route (via `createDashboardActions`)
// owns the PATCH and the re-read. Only the NAME half of the patch is offered:
// the SPA renders layouts, it does not author them.

export type DashboardRenameModalProps = {
  readonly open: boolean;
  /** The board's current name — seeds the field each time the dialog opens. */
  readonly name: string;
  readonly pending: boolean;
  /** The API's reason for refusing (409 on a built-in), shown inline. */
  readonly error?: string | null;
  readonly onRename: (name: string) => void;
  readonly onClose: () => void;
};

export function DashboardRenameModal(props: DashboardRenameModalProps): JSX.Element {
  const [draft, setDraft] = createSignal('');

  // The dialog stays MOUNTED between boards (so `useClosing` can play its exit),
  // so the field is seeded on each open rather than at mount. `untrack` keeps a
  // list refetch mid-edit from clobbering what the reader is typing.
  createEffect(() => {
    if (props.open) setDraft(untrack(() => props.name));
  });

  const valid = (): boolean => draft().trim().length > 0;

  const submit = (e: Event): void => {
    e.preventDefault();
    if (!valid() || props.pending) return;
    props.onRename(draft());
  };

  return (
    <Modal
      open={props.open}
      title={t('dashboards.rename_title')}
      desc={t('dashboards.rename_desc')}
      onClose={props.onClose}
    >
      <form class="dashboard-rename__form" onSubmit={submit}>
        <Field label={t('dashboards.form_name')} id="rename-dashboard-name">
          {(a11y) => (
            <input
              {...a11y}
              class="input"
              type="text"
              maxLength={200}
              value={draft()}
              onInput={(e) => setDraft(e.currentTarget.value)}
              required
            />
          )}
        </Field>

        {props.error ? (
          <p class="field__error" role="alert">
            {props.error}
          </p>
        ) : null}

        <div class="dialog__footer">
          <Button variant="outline" onClick={props.onClose}>
            {t('dashboards.cancel')}
          </Button>
          <button type="submit" class={buttonClass()} disabled={!valid() || props.pending}>
            {t(props.pending ? 'dashboards.renaming' : 'dashboards.rename_submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
